import * as Sentry from "@sentry/nextjs";
import { getStops } from "../../../app/api/getStops";
import { BaseAction } from "../../../core/actions/BaseAction";
import { getRuntimeConfig } from "../../../Lib/Config/RuntimeConfig";
import { updateRecoilState } from "../../../Orchestrator/Recoils/Events/Recoil";
import { IApiResponse, IStop, StationsListType, StationType } from "../../../Orchestrator/Types/Index";

export const transformStops = (stops: IStop[], stopExternalCode: string): StationsListType => {
    if (!stopExternalCode) {
        return [];
    }

    const getMappedStopCode = (stop: IStop): string | undefined => {
        const mappedCode = stop?.externalCodes?.[stopExternalCode];
        if (typeof mappedCode !== "string") {
            return undefined;
        }

        const normalizedCode = mappedCode.trim();
        return normalizedCode.length > 0 ? normalizedCode : undefined;
    };

    // Old Implementation
    // const filterStops = [...stops].filter((stop: IStop) => !(stop?.attributes || []).find((d: any) => d.code === "SHU"));

    // This will not work because cityName and DefaultName can be different and not able to identify the stops correctly.
    // const filterStops = [...stops].filter((stop: IStop) => stop.cityName === stop.defaultName);

    // New Implementation: in which first result will be stop and whatever comes next will be substation
    let listOfShuttles = [...stops].filter((stop: IStop) => {
        if (stop?.attributes && (stop.attributes as any[]).find((d: any) => d.code === "SHT") && getMappedStopCode(stop)) {
            return true;
        }
        return false;
    });
    let listOfStops = (() => {
        let shuttleStationId = listOfShuttles.map((stop: IStop) => stop.id);
        return stops.filter((stop: IStop) => {
            return !shuttleStationId.includes(stop.id)
        });
    })();

    const modifiedStops = [...listOfStops].map((stop: IStop) => {
        const mappedStopCode = getMappedStopCode(stop);
        if (!mappedStopCode) {
            return undefined;
        }

        const stopDetail: any = {
            id: mappedStopCode,
            name: stop.defaultName,
            cityName: stop.cityName,
            showDropdown: false,
            subStations: [],
            attributes: stop.attributes,
            countryName: stop.countryName,
        };

        // Old
        // const listOfShuttle = [...stops].filter((stop: IStop) => (stop?.attributes || []).find((d: any) => d.code === "SHU"));
        const stopShuttle = listOfShuttles.filter(d => d.cityName === stop.cityName);

        if (stopShuttle.length > 0) {
            stopDetail.subStations = stopShuttle
                .map((shu: IStop) => {
                    const mappedShuttleCode = getMappedStopCode(shu);
                    if (!mappedShuttleCode) {
                        return undefined;
                    }

                    return {
                        id: mappedShuttleCode,
                        name: shu.defaultName,
                        cityName: shu.cityName,
                        countryName: stop.countryName,
                        type: "bus"
                    };
                })
                .filter(Boolean);
            stopDetail.subStations.unshift({
                id: mappedStopCode,
                name: stopDetail.id === "PMB" ? stop.cityName : stop.defaultName,
                cityName: stop.cityName,
                countryName: stop.countryName,
                type: "train"
            });
        }
        return stopDetail;
    });
    const filteredModifiedStops = modifiedStops.filter(stop => stop !== undefined);

    // Move stops with subStations (length > 0) to the top, others after
    const stopsWithSubStations = filteredModifiedStops.filter(stop => Array.isArray(stop.subStations) && stop.subStations.length > 0);
    const stopsWithoutSubStations = filteredModifiedStops.filter(stop => !Array.isArray(stop.subStations) || stop.subStations.length === 0);

    return [...stopsWithSubStations, ...stopsWithoutSubStations];
};
class GetStopsAction extends BaseAction<StationType[], void> {
    // Overriding `run` and returning the transformed stops
    override async run(): Promise<StationType[]> {
        let listOfStops: StationType[] = []
        try {
            const config = await getRuntimeConfig();
            if (!config.NEXT_PUBLIC_STOP_EXTERNAL_CODE) {
                console.warn("NEXT_PUBLIC_STOP_EXTERNAL_CODE is missing. Stops list will remain empty.");
                updateRecoilState<StationsListType>("stopsResponseAtom", []);
                return [];
            }
            const response: IApiResponse<{ stops: IStop[] }> = await getStops();
            if (response.status === "ok" && response.answer) {
                const stops = response.answer.stops;
                if (stops.length > 0) {
                    let StationLists = transformStops(stops, config.NEXT_PUBLIC_STOP_EXTERNAL_CODE)
                    updateRecoilState<StationsListType>("stopsResponseAtom", StationLists);
                    listOfStops = StationLists
                } else {
                    console.warn("No stops available.");
                }
            } else {
                console.warn("Failed to fetch stops.");
                Sentry.captureMessage("GetStopsAction: failed to fetch stops", {
                    level: "warning",
                    tags: { action: "GetStopsAction" },
                    extra: { responseStatus: response.status },
                });
            }
        } catch (error: any) {
            console.error("Error fetching stops:", error);
            Sentry.captureException(error, { tags: { action: "GetStopsAction" } });
        } finally {
            return listOfStops;
        }
    }
}

export const stopAction = new GetStopsAction()
